import React, { useEffect, useMemo } from 'react';
import {
    Button,
    Grid,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useGetIssueTrackerRulesQuery } from '../IssueTrackerRuleSlice';
import IssueTrackerRule from './IssueTrackerRule';
import EmptyPlaceholder from 'components/EmptyPlaceholder/EmptyPlaceholder';
import { issueTrackerConfigStyles } from '../style';
import ContractHeader from '../../ContractHeader/ContractHeader';
import LoadingSpinner from 'components/LoadingSpinner';
import { sortBy } from 'lodash';
import Authorize from 'components/Authorize';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import DropdownFilter from "components/DropdownFilter"; // Import the DropdownFilter component
import { issueTrackerRuleTypeList, issueTrackerSystemRuleList, ruleOperandsEnum } from '../IssueTrackerConfigConsts';
import FilterService from 'services/filterService';
import { useSelector } from 'react-redux';
import { hasAccessToAllClients } from 'features/user/userSlice';

const IssueTrackerRulesList = ({ clientConfig }) => {
    const classes = issueTrackerConfigStyles();
    let canSeeAllClients = useSelector(hasAccessToAllClients);

    let { data: issuesTrackerRules, error: issuesTrackerError, isFetching: isLoadingIssuesTracker } = useGetIssueTrackerRulesQuery({ clientAccountId: clientConfig.accountId });
    issuesTrackerRules = issuesTrackerRules || [];

    issuesTrackerRules = useMemo(() => issuesTrackerRules?.map(rule => {
        return {
            ...rule,
            systemRule: rule?.clientAccountId ? 1 : 2,
        }
    }), [issuesTrackerRules]);

    const [rulesList, setRulesList] = React.useState([...sortBy(issuesTrackerRules, ["systemRule", (rule) => -rule.id])]);
    const [filters, setFilters] = React.useState({}); // State for the selected filter

    useEffect(() => {
        //sort the issue tracker rules first by systemRule and then by id in descending order
        setRulesList(sortBy(issuesTrackerRules, ["systemRule", (rule) => -rule.id]) ?? []);
    }, [issuesTrackerRules]);

    const onAdd = () => {
        setRulesList([
            {
                clientAccountId: clientConfig.accountId,
                isNew: true,
                ruleOperand: ruleOperandsEnum.And,
                conditionList: [{ 'field': null, 'value': null }]
            },
            ...rulesList
        ])
    }

    const onCancel = () => {
        setRulesList([...rulesList?.filter(l => !l?.isNew)])
    }

    const onFilter = (filterName, filterValue) => {
        setFilters({ ...filters, [filterName]: { value: filterValue } })
    }

    let filterConfig = [
        { type: 'text', size: 2, key: 'ruleType', values: issueTrackerRuleTypeList },
    ]

    if (canSeeAllClients) {
        filterConfig.push({ type: 'text', size: 2, key: 'systemRule', values: issueTrackerSystemRuleList });
    }

    const filteredRulesList = FilterService.filter(filters, undefined, rulesList, filterConfig);

    return (
        <Grid container direction='column' spacing={2} className={classes.lotContainer}>
            <LoadingSpinner loading={isLoadingIssuesTracker} />
            <Grid container justifyContent="space-between" className={classes.headerContainer}>
                <Grid item>
                    <ContractHeader
                        label={`Issue Tracker Rules (${filteredRulesList?.length ?? 0})`}
                    />
                </Grid>
                <Grid item>
                    <Authorize profile={permissionProfiles.ISSUETRACKER.SAVE_CONFIG}>
                        <Button
                            className={classes.addButton}
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={onAdd}
                            disabled={rulesList?.find(a => a.isNew)}
                        >
                            Add Rule
                        </Button>
                    </Authorize>
                </Grid>

            </Grid>
            <Grid item container justifyContent="space-between" spacing={2}>
                <Grid item xs={6} >
                    <DropdownFilter
                        identifier={"ruleType"}
                        title="Rule Type"
                        onSelect={(val) => onFilter("ruleType", +val)} // Handle filter selection
                        values={issueTrackerRuleTypeList}
                        value={filters?.["ruleType"]?.value}
                        showEmpty
                    />
                </Grid>
                <Grid item xs={6} >
                    {canSeeAllClients &&
                        <DropdownFilter
                            identifier={"systemRule"}
                            title="System Rule"
                            onSelect={(val) => onFilter("systemRule", +val)} // Handle filter selection
                            values={issueTrackerSystemRuleList}
                            value={filters?.["systemRule"]?.value ?? ''}
                            showEmpty
                        />}
                </Grid>
            </Grid>
            <Grid item>
                {
                    filteredRulesList?.map(rule => <IssueTrackerRule key={rule.id} rule={rule} onCancel={onCancel} rulesList={rulesList} clientConfig={clientConfig} />)
                }
                {!(!!filteredRulesList?.length) && <EmptyPlaceholder text="No Rules Defined" />}
            </Grid>
        </Grid>
    );
};

export default IssueTrackerRulesList;
